import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../components/AuthContext";
import WeeklySummaryV3 from "../components/cotpro/WeeklySummaryV3";
import GrossContracts from "../components/cotpro/GrossContracts";
import CotProDashboard from "../components/cotpro/CotProDashboard";
import ContractGuide from "../components/cotpro/ContractGuide";
import ChartBoardReadMe from "../components/cotpro/ChartBoardReadMe";
import AppFooter from "../components/AppFooter";
import { LogOut } from "lucide-react";

const TABS = [
    { key: "summary", label: "Weekly Summary" },
    { key: "gross", label: "Gross Contracts (2×OI)" },
    { key: "dashboard", label: "Charting Board" },
    { key: "directory", label: "Directory" },
];

const pillStyle = {
    background: "none",
    border: "1px solid rgba(184,212,208,0.2)",
    borderRadius: 999,
    padding: "10px 24px",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 400,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    fontFamily: "'DM Sans', sans-serif",
    color: "rgba(184,212,208,0.55)",
    transition: "all 200ms",
};

export default function CotProPage() {
    const [tab, setTab] = useState("summary");
    const [readMeOpen, setReadMeOpen] = useState(false);
    const [latestDate, setLatestDate] = useState("");
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const handleLogout = async () => {
        await logout();
        navigate("/");
    };

    // Fetch latest date from data
    useState(() => {
        const API = process.env.REACT_APP_BACKEND_URL;
        fetch(`${API}/api/cot-pro/v3-summary`).then(r => r.json()).then(d => {
            if (d?.[0]?.date) {
                const parts = d[0].date.split("-");
                setLatestDate(`${parts[1]}/${parts[2]}/${parts[0]}`);
            }
        }).catch(() => {});
    }, []);

    return (
        <div data-testid="cot-pro-page" style={{ minHeight: "100vh", background: "var(--pord-bg, #222518)", display: "flex", flexDirection: "column" }}>
            {/* Top bar — CoT Reports title + nav buttons on same line */}
            <div style={{ padding: "14px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h1 className="pord-heading" style={{ fontSize: "clamp(18px, 2.2vw, 24px)", color: "#fff", margin: 0 }}>
                    CoT Reports
                    {latestDate && (
                        <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, fontWeight: 400, letterSpacing: "0.06em", color: "var(--pord-teal, #b8d4d0)", marginLeft: 16 }}>
                            data as of: {latestDate}
                        </span>
                    )}
                </h1>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <button data-testid="nav-subscribe" onClick={() => navigate("/subscribe")}
                        style={{ ...pillStyle, background: "rgba(221,254,206,0.08)", borderColor: "rgba(221,254,206,0.25)", color: "#DDFECE" }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(221,254,206,0.15)"; e.currentTarget.style.borderColor = "rgba(221,254,206,0.4)"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(221,254,206,0.08)"; e.currentTarget.style.borderColor = "rgba(221,254,206,0.25)"; }}>
                        Subscribe
                    </button>
                    <button data-testid="nav-index" onClick={() => navigate("/index")} style={pillStyle}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(184,212,208,0.4)"; e.currentTarget.style.color = "rgba(184,212,208,0.8)"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(184,212,208,0.2)"; e.currentTarget.style.color = "rgba(184,212,208,0.55)"; }}>
                        Index
                    </button>
                    {user ? (
                        <button data-testid="signout-btn" onClick={handleLogout} style={{ ...pillStyle, display: "flex", alignItems: "center", gap: 6 }}
                            onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(184,212,208,0.4)"; e.currentTarget.style.color = "rgba(184,212,208,0.8)"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(184,212,208,0.2)"; e.currentTarget.style.color = "rgba(184,212,208,0.55)"; }}>
                            <LogOut size={13} /> Sign Out
                        </button>
                    ) : null}
                </div>
            </div>
            <div style={{ borderBottom: "1px solid rgba(184,212,208,0.06)" }} />

            <div style={{ flex: 1, maxWidth: 1400, width: "100%", margin: "0 auto", padding: "12px 24px 40px" }}>
                <div data-testid="cot-pro-tabs" style={{ display: "flex", gap: 4, marginBottom: 20, borderBottom: "1px solid rgba(184,212,208,0.10)", paddingBottom: 0 }}>
                    {TABS.map((t) => (
                        <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
                            style={{
                                fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 400, letterSpacing: "0.06em", textTransform: "uppercase",
                                padding: "12px 20px", background: "none", border: "none",
                                borderBottom: tab === t.key ? "2px solid var(--pord-teal)" : "2px solid transparent",
                                color: tab === t.key ? "var(--pord-teal)" : "rgba(255,255,255,0.4)",
                                cursor: "pointer", transition: "all 200ms ease",
                            }}>
                            {t.label}
                        </button>
                    ))}
                </div>

                {/* Tab content — all mounted, only active visible (preserves state) */}
                <div style={{ display: tab === "summary" ? "block" : "none" }}>
                    <WeeklySummaryV3 />
                </div>
                <div style={{ display: tab === "gross" ? "block" : "none" }}><GrossContracts /></div>
                <div style={{ display: tab === "dashboard" ? "block" : "none" }}><CotProDashboard /></div>
                <div style={{ display: tab === "directory" ? "block" : "none" }}><ContractGuide /></div>
                {readMeOpen && <ChartBoardReadMe onClose={() => setReadMeOpen(false)} />}
                <AppFooter />
            </div>
        </div>
    );
}
